// src/components/DocumentList.jsx
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  FileText,
  FileImage,
  File,
  FileSpreadsheet,
  FileArchive,
  Download,
  Trash2,
  Eye,
  Upload,
  Plus,
  MoreVertical,
  CheckCircle,
  AlertCircle,
  Clock,
  User,
  Tag,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/useAuth';
import { useNotifications } from '../contexts/NotificationsContext';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useIsMobile } from '../hooks/useMediaQuery';

/**
 * 🎯 Componente DocumentList - Gerenciamento profissional de documentos
 * 
 * 🔥 V5.1: Soporta prop documents externa para sincronización en tiempo real
 * desde ClientDetails.jsx. Si no recibe la prop, usa estado interno (standalone).
 * 
 * Funcionalidades:
 * - 📁 Upload de documentos com preview
 * - 🗂️ Organização por tipo/categoria
 * - 🔍 Visualização inline de imagens/PDFs
 * - 📊 Estatísticas de armazenamento
 * - 🏷️ Tags e categorização
 * - 🔄 Drag & drop upload
 * - ⚡ Ações rápidas (download, excluir, visualizar)
 * - 📱 Responsivo - ADAPTADO
 */

// Ícones por tipo de arquivo
const FILE_ICONS = {
  'application/pdf': <FileText className="w-5 h-5 text-red-500" />,
  'image/jpeg': <FileImage className="w-5 h-5 text-blue-500" />,
  'image/png': <FileImage className="w-5 h-5 text-blue-500" />,
  'image/gif': <FileImage className="w-5 h-5 text-blue-500" />,
  'image/webp': <FileImage className="w-5 h-5 text-blue-500" />,
  'application/msword': <FileText className="w-5 h-5 text-blue-700" />,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': <FileText className="w-5 h-5 text-blue-700" />,
  'application/vnd.ms-excel': <FileSpreadsheet className="w-5 h-5 text-green-700" />,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': <FileSpreadsheet className="w-5 h-5 text-green-700" />,
  'application/zip': <FileArchive className="w-5 h-5 text-amber-600" />,
  'application/x-rar-compressed': <FileArchive className="w-5 h-5 text-amber-600" />
};

const DEFAULT_ICON = <File className="w-5 h-5 text-slate-400" />;

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileTypeCategory = (mimeType) => {
  if (mimeType?.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType?.includes('word')) return 'document';
  if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return 'spreadsheet';
  if (mimeType?.includes('zip') || mimeType?.includes('rar')) return 'archive';
  return 'other';
};

export const DocumentList = ({
  clientId,
  propertyId,
  onUpdate,
  maxHeight = '400px',
  showUpload = true,
  showStats = true,
  allowDelete = true,
  allowDownload = true,
  className = '',
  // 🔥 V5.1: Prop externa para sincronización en tiempo real
  documents: externalDocuments,
  // 🔥 V5.1: Loading externo opcional
  loading: externalLoading
}) => {
  console.log('[V5.1] DocumentList - externalDocuments:', externalDocuments?.length, 'externalLoading:', externalLoading);

  // ==========================================================
  // Hooks e Contextos
  // ==========================================================
  const { database, openConfirm } = useAuth();
  const isMobile = useIsMobile();
  const notificationsCtx = (() => {
    try {
      return useNotifications();
    } catch {
      return null;
    }
  })();

  const notify = notificationsCtx?.notifySystem ||
    notificationsCtx?.notify ||
    ((msg) => console.log('📄 Documento:', msg));

  // ==========================================================
  // Estados
  // ==========================================================
  // 🔥 V5.1: Estado interno como fallback (cuando NO hay prop documents)
  const [internalDocuments, setInternalDocuments] = useState([]);
  const [internalLoading, setInternalLoading] = useState(true);
  
  // 🔥 V5.1: Fuente principal: prop del padre; fallback: estado interno
  const documents = externalDocuments !== undefined ? externalDocuments : internalDocuments;
  const loading = externalLoading !== undefined ? externalLoading : internalLoading;
  
  const [uploading, setUploading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dragging, setDragging] = useState(false);

  // ==========================================================
  // Carregar documentos (solo cuando NO hay prop externa)
  // ==========================================================
  const loadDocuments = useCallback(async () => {
    // 🔥 V5.1: Si hay prop externa, no cargar autónomamente
    if (externalDocuments !== undefined) {
      console.log('[V5.1] DocumentList: usando prop documents del padre, skip carga autónoma');
      setInternalLoading(false);
      return;
    }

    if (!database) return;

    try {
      setInternalLoading(true);
      let docs = [];

      if (clientId && typeof database.getDocumentsByClient === 'function') {
        docs = await database.getDocumentsByClient(clientId);
      } else if (propertyId && typeof database.getDocumentsByProperty === 'function') {
        docs = await database.getDocumentsByProperty(propertyId);
      } else if (typeof database.getDocuments === 'function') {
        docs = await database.getDocuments();
      }

      docs = Array.isArray(docs) ? docs : [];
      docs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setInternalDocuments(docs);
      console.log('[V5.1] DocumentList: carga autónoma completada:', docs.length);
    } catch (error) {
      console.error('❌ Erro ao carregar documentos:', error);
      notify({
        type: 'danger',
        title: 'Erro ao carregar documentos',
        message: 'Não foi possível carregar a lista de documentos.'
      });
    } finally {
      setInternalLoading(false);
    }
  }, [database, clientId, propertyId, notify, externalDocuments]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // ==========================================================
  // Estatísticas
  // ==========================================================
  const stats = useMemo(() => {
    const total = documents.length;
    const totalSize = documents.reduce((acc, doc) => {
      const size = doc.base64 ? Math.round((doc.base64.length * 3) / 4) : 0;
      return acc + size;
    }, 0);

    const byType = documents.reduce((acc, doc) => {
      const type = getFileTypeCategory(doc.fileType);
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    return { total, totalSize, byType };
  }, [documents]);

  // ==========================================================
  // Filtrar documentos
  // ==========================================================
  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      if (filter !== 'all' && getFileTypeCategory(doc.fileType) !== filter) {
        return false;
      }

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return doc.title?.toLowerCase().includes(term) ||
          doc.fileName?.toLowerCase().includes(term);
      }

      return true;
    });
  }, [documents, filter, searchTerm]);

  // ==========================================================
  // Upload de documentos
  // ==========================================================
  const handleFileUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (file.size > 5 * 1024 * 1024) {
          notify({
            type: 'warning',
            title: 'Arquivo muito grande',
            message: `${file.name} excede 5MB. Tamanho máximo: 5MB`
          });
          continue;
        }

        const reader = new FileReader();
        const base64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const newDoc = database.createDocument({
          title: file.name,
          fileName: file.name,
          fileType: file.type,
          base64: base64,
          clientId: clientId || null,
          propertyId: propertyId || null
        });

        if (newDoc) {
          notify({
            type: 'success',
            title: 'Documento enviado',
            message: `${file.name} foi adicionado com sucesso.`
          });
        }
      }

      // 🔥 V5.1: Recargar solo si estamos en modo autónomo
      if (externalDocuments === undefined) {
        loadDocuments();
      }
      onUpdate?.();
    } catch (error) {
      console.error('❌ Erro no upload:', error);
      notify({
        type: 'danger',
        title: 'Erro no upload',
        message: 'Não foi possível enviar o arquivo. Tente novamente.'
      });
    } finally {
      setUploading(false);
    }
  }, [database, clientId, propertyId, notify, loadDocuments, onUpdate, externalDocuments]);

  // ==========================================================
  // Excluir documento
  // ==========================================================
  const handleDelete = useCallback((doc) => {
    openConfirm({
      title: 'Excluir documento',
      message: `Tem certeza que deseja excluir "${doc.title || doc.fileName}"?`,
      type: 'warning',
      onConfirm: () => {
        try {
          database.deleteDocument(doc.id);
          
          // 🔥 V5.1: Recargar solo si estamos en modo autónomo
          if (externalDocuments === undefined) {
            loadDocuments();
          }
          onUpdate?.();

          notify({
            type: 'success',
            title: 'Documento excluído',
            message: 'O documento foi removido com sucesso.'
          });
        } catch (error) {
          console.error('❌ Erro ao excluir:', error);
          notify({
            type: 'danger',
            title: 'Erro ao excluir',
            message: 'Não foi possível excluir o documento.'
          });
        }
      }
    });
  }, [database, loadDocuments, notify, onUpdate, openConfirm, externalDocuments]);

  // ==========================================================
  // Download documento
  // ==========================================================
  const handleDownload = useCallback((doc) => {
    try {
      const link = document.createElement('a');
      link.href = doc.base64;
      link.download = doc.fileName || doc.title || 'documento';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notify({
        type: 'success',
        title: 'Download iniciado',
        message: 'O download do documento foi iniciado.'
      });
    } catch (error) {
      console.error('❌ Erro no download:', error);
      notify({
        type: 'danger',
        title: 'Erro no download',
        message: 'Não foi possível baixar o documento.'
      });
    }
  }, [notify]);

  // ==========================================================
  // Drag & Drop handlers
  // ==========================================================
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  }, [handleFileUpload]);

  // ==========================================================
  // Renderização - ADAPTADA
  // ==========================================================
  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden ${className}`}>
      
      {/* Header - ADAPTADO */}
      <div className={`border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white ${
        isMobile ? 'px-4 py-3' : 'px-6 py-4'
      }`}>
        <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center justify-between'}`}>
          <div className="flex items-center gap-2">
            <div className={`${isMobile ? 'p-1.5' : 'p-2'} bg-blue-100 rounded-xl`}>
              <FileText className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} style={{ color: '#2563eb' }} />
            </div>
            <div>
              <h2 className={`font-bold text-slate-900 ${isMobile ? 'text-sm' : 'text-lg'}`}>
                {isMobile ? 'Docs' : 'Documentos'}
              </h2>
              <p className={`text-slate-500 ${isMobile ? 'text-[9px]' : 'text-xs'}`}>
                {stats.total} {stats.total === 1 ? 'doc' : 'docs'} • {formatFileSize(stats.totalSize)}
              </p>
            </div>
          </div>

          {/* Filtros rápidos - scroll horizontal en móvil */}
          <div className={`flex ${isMobile ? 'overflow-x-auto pb-1 gap-1' : 'gap-1'}`}>
            {['all', 'image', 'pdf', 'document'].map((type) => (
              <button
                key={type}
                onClick={() => setFilter(type)}
                className={`whitespace-nowrap rounded-lg transition-colors ${
                  filter === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                } ${isMobile ? 'px-2 py-1 text-[9px]' : 'px-3 py-1.5 text-xs'}`}
              >
                {type === 'all' ? 'Todos' : type === 'image' ? 'Imagens' : type}
              </button>
            ))}
          </div>
        </div>

        {/* Busca */}
        <div className={`${isMobile ? 'mt-2' : 'mt-4'}`}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={isMobile ? "Buscar..." : "Buscar documentos..."}
            className={`w-full border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-200 ${
              isMobile ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'
            }`}
          />
        </div>
      </div>

      {/* Área de upload - ADAPTADA */}
      {showUpload && (
        <div
          className={`border-b border-slate-200 transition-colors ${
            isMobile ? 'p-3' : 'p-4'
          } ${dragging ? 'bg-blue-50 border-2 border-blue-300 border-dashed' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <label className="cursor-pointer block">
            <input
              type="file"
              multiple
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip"
            />
            <div className={`flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl hover:border-blue-300 transition-colors ${
              isMobile ? 'py-2 px-2' : 'py-4'
            }`}>
              {uploading ? (
                <>
                  <div className={`animate-spin rounded-full border-2 border-blue-600 border-t-transparent ${
                    isMobile ? 'w-3 h-3' : 'w-5 h-5'
                  }`} />
                  <span className={`text-slate-600 ${isMobile ? 'text-[9px]' : 'text-sm'}`}>
                    {isMobile ? 'Enviando' : 'Enviando...'}
                  </span>
                </>
              ) : (
                <>
                  <Upload className={`${isMobile ? 'w-3 h-3' : 'w-5 h-5'} text-blue-600`} />
                  <span className={`text-slate-600 ${isMobile ? 'text-[9px]' : 'text-sm'}`}>
                    {isMobile ? 'Upload' : 'Clique para enviar ou arraste arquivos'}
                  </span>
                  {!isMobile && (
                    <span className="text-xs text-slate-400">
                      (PDF, DOC, XLS, Imagens até 5MB)
                    </span>
                  )}
                </>
              )}
            </div>
          </label>
        </div>
      )}

      {/* Lista de documentos - ADAPTADA */}
      <div
        className="overflow-y-auto"
        style={{ maxHeight: isMobile ? '300px' : maxHeight }}
      >
        {loading ? (
          <div className={`${isMobile ? 'p-4 space-y-2' : 'p-8 space-y-3'}`}>
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse">
                <div className={`${isMobile ? 'h-12' : 'h-16'} bg-slate-100 rounded-xl`}></div>
              </div>
            ))}
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className={`text-center ${isMobile ? 'py-8 px-3' : 'py-12 px-4'}`}>
            <div className={`mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-3 ${
              isMobile ? 'w-12 h-12' : 'w-16 h-16'
            }`}>
              <FileText className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} text-slate-400`} />
            </div>
            <h3 className={`font-medium text-slate-700 mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              {isMobile ? 'Sem documentos' : 'Nenhum documento'}
            </h3>
            <p className={`text-slate-400 max-w-sm mx-auto ${isMobile ? 'text-[9px]' : 'text-xs'}`}>
              {searchTerm || filter !== 'all'
                ? 'Nada encontrado'
                : isMobile ? 'Arraste ou clique para upload' : 'Arraste arquivos ou clique no botão acima para adicionar documentos.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredDocuments.map((doc) => (
              <div
                key={doc.id}
                className={`hover:bg-slate-50 transition-colors group ${
                  isMobile ? 'p-3' : 'p-4'
                }`}
              >
                <div className="flex items-start gap-2">
                  {/* Ícone */}
                  <div className="flex-shrink-0">
                    {FILE_ICONS[doc.fileType] || DEFAULT_ICON}
                  </div>

                  {/* Informações */}
                  <div className="flex-1 min-w-0">
                    <div className={`flex ${isMobile ? 'flex-col' : 'items-start justify-between'} gap-1`}>
                      <div className="min-w-0 flex-1">
                        <h4 className={`font-medium text-slate-900 truncate ${
                          isMobile ? 'text-xs' : 'text-sm'
                        }`}>
                          {doc.title || doc.fileName}
                        </h4>
                        <div className={`flex items-center gap-1 text-slate-500 ${
                          isMobile ? 'text-[8px] flex-wrap' : 'text-xs mt-1'
                        }`}>
                          <span>{formatFileSize(doc.base64 ? Math.round((doc.base64.length * 3) / 4) : 0)}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Clock className={isMobile ? 'w-2 h-2' : 'w-3 h-3'} />
                            {isMobile 
                              ? formatDistanceToNow(new Date(doc.createdAt), { addSuffix: true }).substring(0, 6)
                              : formatDistanceToNow(new Date(doc.createdAt), { addSuffix: true, locale: ptBR })}
                          </span>
                        </div>
                      </div>

                      {/* Ações - SIEMPRE VISIBLES EN MÓVIL */}
                      <div className={`flex gap-1 ${isMobile ? '' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                        {doc.fileType?.startsWith('image/') && (
                          <button
                            onClick={() => setPreviewDoc(doc)}
                            className={`hover:bg-blue-100 rounded-lg transition-colors ${
                              isMobile ? 'p-1' : 'p-2'
                            }`}
                            title="Visualizar"
                          >
                            <Eye className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-blue-600`} />
                          </button>
                        )}
                        {allowDownload && (
                          <button
                            onClick={() => handleDownload(doc)}
                            className={`hover:bg-green-100 rounded-lg transition-colors ${
                              isMobile ? 'p-1' : 'p-2'
                            }`}
                            title="Download"
                          >
                            <Download className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-green-600`} />
                          </button>
                        )}
                        {allowDelete && (
                          <button
                            onClick={() => handleDelete(doc)}
                            className={`hover:bg-red-100 rounded-lg transition-colors ${
                              isMobile ? 'p-1' : 'p-2'
                            }`}
                            title="Excluir"
                          >
                            <Trash2 className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-red-600`} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de preview - ADAPTADO */}
      {previewDoc && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2"
          onClick={() => setPreviewDoc(null)}
        >
          <div
            className={`bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden ${
              isMobile ? 'mx-2' : ''
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between border-b border-slate-200 ${
              isMobile ? 'px-3 py-2' : 'px-6 py-4'
            }`}>
              <h3 className={`font-semibold text-slate-900 truncate max-w-[200px] ${
                isMobile ? 'text-sm' : ''
              }`}>
                {previewDoc.title || previewDoc.fileName}
              </h3>
              <button
                onClick={() => setPreviewDoc(null)}
                className={`hover:bg-slate-100 rounded-lg transition-colors ${
                  isMobile ? 'p-1' : 'p-2'
                }`}
              >
                <X className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} />
              </button>
            </div>
            <div className={`overflow-auto max-h-[calc(90vh-80px)] ${
              isMobile ? 'p-2' : 'p-6'
            }`}>
              {previewDoc.fileType?.startsWith('image/') ? (
                <img
                  src={previewDoc.base64}
                  alt={previewDoc.title}
                  className="max-w-full h-auto rounded-lg"
                />
              ) : (
                <iframe
                  src={previewDoc.base64}
                  title={previewDoc.title}
                  className={`w-full rounded-lg ${isMobile ? 'h-[50vh]' : 'h-[70vh]'}`}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Versão simplificada para listagem rápida - ADAPTADA
DocumentList.Simple = ({ documents = [], onDownload, onDelete, isMobile }) => (
  <div className="divide-y divide-slate-100">
    {documents.map((doc) => (
      <div key={doc.id} className={`flex items-center justify-between ${
        isMobile ? 'py-2' : 'py-2'
      }`}>
        <div className="flex items-center gap-2 min-w-0">
          {FILE_ICONS[doc.fileType] || DEFAULT_ICON}
          <span className={`text-slate-700 truncate ${isMobile ? 'text-xs' : 'text-sm'}`}>
            {doc.title || doc.fileName}
          </span>
        </div>
        <div className="flex gap-1">
          {onDownload && (
            <button onClick={() => onDownload(doc)} className={`hover:bg-slate-100 rounded ${
              isMobile ? 'p-1' : 'p-1'
            }`}>
              <Download className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} />
            </button>
          )}
          {onDelete && (
            <button onClick={() => onDelete(doc)} className={`hover:bg-red-100 rounded ${
              isMobile ? 'p-1' : 'p-1'
            }`}>
              <Trash2 className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} style={{ color: '#ef4444' }} />
            </button>
          )}
        </div>
      </div>
    ))}
  </div>
);

export default DocumentList;